import { useEffect, useMemo, useState } from 'react';
import { useLocation, useRoutes } from 'react-router-dom';
import { useUrlSearchParams } from 'use-url-search-params';
import { Layouts } from '../../@crema/components/AppLayout';
import ModifiedAppContentView from '../../@crema/components/ModifiedAppContentView';
import {
    useLayoutActionsContext,
    useLayoutContext,
} from '../../@crema/context/AppContextProvider/LayoutContextProvider';
import { useSidebarActionsContext } from '../../@crema/context/AppContextProvider/SidebarContextProvider';
import generateRoutes from '../../@crema/helpers/RouteGenerator';
import { useHierarchyMode } from '../context/HierarchyModeContext';
import {
    getCanShowHierarchyToggle,
    getCompanyZoneEnabled,
    getDefaultModeForUser,
} from '../helpers/hierarchyModeHelper';
import { generateAuthDetails } from '../helpers/secureKeyHelper';
import { useAuthUser } from '../hook/AuthHooks';
import {
    anonymousStructure,
    authorizedStructure,
    publicStructure,
} from '../routes';
import routesConfig from '../routes/routeConfig';

const MainAppLayout: React.FC = () => {
    const { navStyle } = useLayoutContext();
    const { user, isAuthenticated } = useAuthUser();
    const { updateNavStyle } = useLayoutActionsContext();
    const { updateMenuStyle, setSidebarBgImage } = useSidebarActionsContext();
    const { updateCanShowHierarchyToggle, setCompanyZoneEnabled, setMode } =
        useHierarchyMode();
    const AppLayoutComponent = Layouts[navStyle];
    const [params] = useUrlSearchParams();
    const { pathname } = useLocation();
    const [loginUrl, setLoginUrl] = useState<string>('');

    const portalURL: string = import.meta.env.VITE__APP_PORTAL_URL;

    const initURL: string = params?.redirect
        ? (params?.redirect as string).toString()
        : import.meta.env.VITE__INITIAL_URL.toString() || '/monitor';

    const clientId = import.meta.env.VITE__KEYCLOAK_CLIENT_ID;
    const authURL = import.meta.env.VITE__KEYCLOAK_LOGIN_BASE_URL;

    useEffect(() => {
        const initializeLogin = async () => {
            const redirectUri = `${portalURL}/authenticate?redirect=${pathname}`;
            const { loginUrl: generatedLoginUrl } = await generateAuthDetails(
                authURL,
                clientId,
                redirectUri,
                pathname,
            );
            setLoginUrl(generatedLoginUrl);
        };

        if (!isAuthenticated) {
            initializeLogin();
        }
    }, [isAuthenticated, pathname, authURL, clientId, portalURL]);

    useEffect(() => {
        if (isAuthenticated && user) {
            const companyZoneEnabled = getCompanyZoneEnabled(user);
            setCompanyZoneEnabled(companyZoneEnabled);

            const canShowToggle = getCanShowHierarchyToggle(
                user,
                companyZoneEnabled,
            );

            updateCanShowHierarchyToggle(canShowToggle);

            if (!canShowToggle) {
                console.log(getDefaultModeForUser(user, companyZoneEnabled));
                setMode(getDefaultModeForUser(user, companyZoneEnabled));
            }
        }
    }, [isAuthenticated, user]);

    const generatedRoutes = useMemo(() => {
        return generateRoutes({
            isAuthenticated,
            userRole: user?.role ?? '',
            anonymousStructure: anonymousStructure(initURL),
            authorizedStructure: authorizedStructure(loginUrl), // Pass loginUrl here
        });
    }, [isAuthenticated, user?.role, initURL, loginUrl]);

    const generatedPublicRoutes = useMemo(() => {
        return generateRoutes({
            isAuthenticated,
            userRole: user?.role ?? '',
            publicStructure: publicStructure(initURL),
        });
    }, [isAuthenticated, user?.role, initURL]);

    const routes = useRoutes(generatedRoutes);

    const publicRoutes = useRoutes(generatedPublicRoutes);

    useEffect(() => {
        if (params.layout) updateNavStyle(params.layout as string);
        if (params.menuStyle) updateMenuStyle(params.menuStyle as string);
        if (params.sidebarImage) setSidebarBgImage(true);
    }, [
        params.layout,
        params.menuStyle,
        params.sidebarImage,
        updateNavStyle,
        updateMenuStyle,
        setSidebarBgImage,
    ]);

    if (!isAuthenticated && !loginUrl) {
        return <div>Loading...</div>; // Show a loading indicator while loginUrl is being generated
    }

    if (!isAuthenticated && pathname !== '/authenticate') {
        window.location.replace(loginUrl);
        return null;
    }

    if (pathname === '/authenticate') {
        return <ModifiedAppContentView routes={publicRoutes} />;
    }

    return <AppLayoutComponent routes={routes} routesConfig={routesConfig} />;
};

export default MainAppLayout;
